// client/src/pages/AdminProductList.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import ProductForm from '../components/ProductForm';
import BulkImportDialog from '../components/BulkImportDialog';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Box, Typography, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, IconButton, Stack, Alert, CircularProgress, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { LOW_STOCK_THRESHOLD } from '../constants/inventory';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const AdminProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const { token } = useContext(AuthContext);

  const fetchProducts = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch products.');
      setProducts(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSaveProduct = async (formData) => {
    const isEditing = !!editingProduct;
    const url = isEditing ? `${API_BASE_URL}/api/products/${editingProduct.id}` : `${API_BASE_URL}/api/products`;
    const method = isEditing ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save product.');
      }
      fetchProducts();
      setIsFormVisible(false);
      setEditingProduct(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller/products/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to export products.');
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'products.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Permanently delete this product?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete product.');
      fetchProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Product Management</Typography>
        {!isFormVisible && (
          <Button variant="contained" onClick={() => { setEditingProduct(null); setIsFormVisible(true); }}>
            Add New Product
          </Button>
        )}
      </Stack>

      <Button size="small" startIcon={<FileUploadOutlinedIcon />} onClick={() => setImportOpen(true)} sx={{ mb: 2 }}>Import products</Button>
      <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport} sx={{ mb: 2, ml: 1 }}>Export products</Button>
      <BulkImportDialog open={importOpen} onClose={() => setImportOpen(false)} token={token} onImported={fetchProducts} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isFormVisible && (
        <ProductForm
          product={editingProduct}
          onSave={handleSaveProduct}
          onCancel={() => { setIsFormVisible(false); setEditingProduct(null); }}
        />
      )}

      {products.length === 0 && !isFormVisible ? (
        <EmptyState
          icon={<Inventory2OutlinedIcon />}
          title="No products yet"
          description="Products added by you or by sellers will show up here."
        />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Price</TableCell><TableCell>Stock</TableCell>
                <TableCell>Seller</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>{product.id}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{formatCurrency(product.price)}</TableCell>
                  <TableCell>
                    {product.quantity ?? 'N/A'}
                    {Number(product.quantity) > 0 && Number(product.quantity) <= LOW_STOCK_THRESHOLD && (
                      <Chip label="Low Stock" color="warning" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{product.store_name || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => { setEditingProduct(product); setIsFormVisible(true); window.scrollTo(0, 0); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(product.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminProductList;
